const uid = (value) => (value == null ? "" : String(value));

export { uid };

export const isIncomingMessage = (chat, openChatUserUid, userUid) => {
  if (chat.isOwned === true) return false;
  if (chat.isOwned === false) return true;
  if (chat.status === "Receive") return true;
  if (chat.status === "Send") return false;

  if (chat.senderUid && chat.receiverUid) {
    return (
      uid(chat.senderUid) === uid(openChatUserUid) &&
      uid(chat.receiverUid) === uid(userUid)
    );
  }

  return false;
};

export const getUnreadIncomingMessages = (chatList, openChatUserUid, userUid) => {
  if (!Array.isArray(chatList) || !openChatUserUid || !userUid) return [];

  const unread = [];
  for (const group of chatList) {
    for (const chat of group?.chats || []) {
      if (chat.isRead === true || chat.isRead === "true") continue;
      if (isIncomingMessage(chat, openChatUserUid, userUid)) {
        unread.push(chat);
      }
    }
  }
  return unread;
};

export const consolidateChatListByDate = (chatList) => {
  if (!Array.isArray(chatList)) return [];

  const byDate = new Map();

  for (const group of chatList) {
    if (!group?.msgDate) continue;

    const dateKey = String(group.msgDate);
    const chats = Array.isArray(group.chats) ? group.chats : [];

    if (!byDate.has(dateKey)) {
      byDate.set(dateKey, { ...group, chats: [...chats] });
      continue;
    }

    const existing = byDate.get(dateKey);
    const seenIds = new Set(existing.chats.map((c) => String(c.chatID)));

    for (const chat of chats) {
      const id = String(chat.chatID);
      if (!seenIds.has(id)) {
        existing.chats.push(chat);
        seenIds.add(id);
      }
    }
  }

  return Array.from(byDate.values()).map((group) => ({
    ...group,
    chats: [...group.chats].sort(
      (a, b) => new Date(a.time) - new Date(b.time)
    ),
  }));
};

export const normalizeChatList = (chatList, partnerUid, userUid) => {
  if (!Array.isArray(chatList)) return [];

  const normalized = chatList.map((group) => ({
    ...group,
    chats: (group?.chats || []).map((chat) => {
      let isOwned;
      if (chat.isOwned === true) {
        isOwned = true;
      } else if (chat.isOwned === false) {
        isOwned = false;
      } else if (chat.senderUid && userUid) {
        isOwned = uid(chat.senderUid) === uid(userUid);
      } else if (chat.status === "Receive") {
        isOwned = false;
      } else {
        isOwned = false;
      }

      return {
        ...chat,
        isOwned,
        senderUid: chat.senderUid || (isOwned ? userUid : partnerUid),
        receiverUid: chat.receiverUid || (isOwned ? partnerUid : userUid),
      };
    }),
  }));

  return consolidateChatListByDate(normalized);
};

export const getUnreadSignature = (unreadMessages) =>
  unreadMessages
    .map((m) => m.chatID)
    .filter((id) => id != null)
    .map((id) => String(id))
    .sort()
    .join(",");

const processedBatches = new Map();
const inFlightKeys = new Set();

export const shouldProcessUnreadBatch = (userId, signature) => {
  if (!signature) return false;
  return processedBatches.get(uid(userId)) !== signature;
};

export const markUnreadBatchProcessed = (userId, signature) => {
  if (userId && signature) {
    processedBatches.set(uid(userId), signature);
  }
};

export const clearProcessedUnreadBatch = (userId) => {
  if (userId) {
    processedBatches.delete(uid(userId));
  }
};

export const acquireReadLock = (key) => {
  if (inFlightKeys.has(key)) return false;
  inFlightKeys.add(key);
  return true;
};

export const releaseReadLock = (key) => {
  inFlightKeys.delete(key);
};

export const toReadReceiptPayload = (messages) => {
  if (messages == null) return [];

  if (messages.responseData != null) {
    return toReadReceiptPayload(messages.responseData);
  }

  const raw = Array.isArray(messages) ? messages : [messages];

  return raw.flatMap((item) => {
    if (item == null) return [];

    if (typeof item === "number" || typeof item === "string") {
      return [{ chatID: item }];
    }

    if (item.chatID != null) {
      return [item];
    }

    return [];
  });
};

export const isReadReceiptRelevant = (item, partnerUid, userUid) => {
  if (!partnerUid || !userUid) return false;

  const me = uid(userUid);
  const partner = uid(partnerUid);

  if (item?.chatID == null) return false;

  if (item.senderUid != null && item.receiverUid != null) {
    const sender = uid(item.senderUid);
    const receiver = uid(item.receiverUid);

    if (sender === me && receiver === partner) return true;
    if (receiver === me && sender === partner) return true;
    return false;
  }

  return true;
};

export const isHubReceiptForMySentMessage = (hubItem, userUid, partnerUid) => {
  if (!hubItem || !userUid || !partnerUid) return false;
  if (hubItem.senderUid == null || hubItem.receiverUid == null) return false;

  const me = uid(userUid);
  const partner = uid(partnerUid);
  const sender = uid(hubItem.senderUid);
  const receiver = uid(hubItem.receiverUid);

  return sender === me && receiver === partner;
};

export const applyReadReceiptsToChatList = (
  prevList,
  readItems,
  userUid,
  partnerUid
) => {
  if (!Array.isArray(prevList)) return prevList;

  const items = toReadReceiptPayload(readItems);
  const readChatIds = new Set(
    items.map((item) => String(item.chatID)).filter((id) => id !== "undefined")
  );

  if (readChatIds.size === 0) return prevList;

  const hubByChatId = new Map(
    items
      .filter((item) => item?.chatID != null)
      .map((item) => [String(item.chatID), item])
  );

  return prevList.map((group) => {
    if (!group) return group;

    if (Array.isArray(group.chats)) {
      return {
        ...group,
        chats: group.chats.map((chat) => {
          const id = String(chat.chatID);
          if (!readChatIds.has(id)) return chat;

          const hubItem = hubByChatId.get(id);

          if (
            hubItem &&
            isHubReceiptForMySentMessage(hubItem, userUid, partnerUid)
          ) {
            return { ...chat, isRead: true };
          }

          if (partnerUid && userUid && isIncomingMessage(chat, partnerUid, userUid)) {
            return chat;
          }

          return { ...chat, isRead: true };
        }),
      };
    }

    if (group.chatID != null && readChatIds.has(String(group.chatID))) {
      return { ...group, isRead: true };
    }

    return group;
  });
};

export const mergeChatListReadStatus = (newList, prevList, userUid, partnerUid) => {
  if (!Array.isArray(newList)) return newList;
  if (!Array.isArray(prevList) || prevList.length === 0) return newList;

  const readSentIds = new Set();
  for (const group of prevList) {
    for (const chat of group?.chats || []) {
      if (chat.isRead !== true || chat.chatID == null) continue;
      if (partnerUid && isIncomingMessage(chat, partnerUid, userUid)) continue;
      readSentIds.add(String(chat.chatID));
    }
  }

  if (readSentIds.size === 0) return newList;

  return newList.map((group) => ({
    ...group,
    chats: (group.chats || []).map((chat) =>
      readSentIds.has(String(chat.chatID)) ? { ...chat, isRead: true } : chat
    ),
  }));
};

export const markAllSentInOpenChatAsRead = (prevList, partnerUid, userUid) => {
  if (!Array.isArray(prevList) || !partnerUid || !userUid) return prevList;

  return prevList.map((group) => ({
    ...group,
    chats: (group.chats || []).map((chat) => {
      if (isIncomingMessage(chat, partnerUid, userUid)) return chat;
      return { ...chat, isRead: true };
    }),
  }));
};

const fullyReadPartners = new Set();

export const cachePartnerSentMessagesRead = (partnerUid) => {
  if (partnerUid) fullyReadPartners.add(uid(partnerUid));
};

export const mergeCachedPartnerReadReceipts = (chatList, partnerUid, userUid) => {
  if (!fullyReadPartners.has(uid(partnerUid))) return chatList;
  return markAllSentInOpenChatAsRead(chatList, partnerUid, userUid);
};

export const resolvePartnerFromReadHub = (hubData, userUid) => {
  if (!userUid) return null;
  const me = uid(userUid);
  const payload = toReadReceiptPayload(hubData);

  for (const item of payload) {
    if (item.senderUid == null || item.receiverUid == null) continue;
    const sender = uid(item.senderUid);
    const receiver = uid(item.receiverUid);
    if (sender === me) return receiver;
    if (receiver === me) return sender;
  }

  const data = Array.isArray(hubData) ? hubData[0] : hubData;
  if (!data || typeof data !== "object") return null;

  const to = uid(data.toUserUid);
  const from = uid(
    data.fromUserUid ?? data.readerUid ?? data.readByUid ?? data.senderUid
  );

  if (from && from !== me) return from;
  if (to && to !== me) return to;
  if (to === me && from) return from;

  return null;
};

export const logReadReceipt = (eventName, hubData, extra = {}) => {
  const userUid = localStorage.getItem("userUid");
  const partner = resolvePartnerFromReadHub(hubData, userUid);
  const items = toReadReceiptPayload(hubData);
  const mySent = items.filter((item) =>
    isHubReceiptForMySentMessage(item, userUid, partner)
  );

  console.warn(`[ReadReceipt] ${eventName}`, {
    hubData,
    userUid,
    openChat: localStorage.getItem("toUserUid"),
    partner,
    mySentChatIds: mySent.map((m) => m.chatID),
    ...extra,
  });
};

export const isBulkReadAllForOpenChat = (hubData, userUid, partnerUid) => {
  if (!partnerUid || !userUid) return false;

  const parsed = toReadReceiptPayload(hubData);
  if (parsed.length > 0) return false;

  const me = uid(userUid);
  const partner = uid(partnerUid);
  const data = Array.isArray(hubData) ? hubData[0] : hubData;

  if (!data || typeof data !== "object") return true;

  const to = uid(data.toUserUid);
  const from = uid(
    data.fromUserUid ?? data.readerUid ?? data.readByUid ?? data.senderUid
  );

  if (to === me || from === partner) return true;

  if (data.msgID != null) {
    const localMsgId = uid(localStorage.getItem("msgId"));
    if (localMsgId && uid(data.msgID) === localMsgId) return true;
  }

  return false;
};

export const isDeleteHubRelevant = (hubMessage, partnerUid, userUid) => {
  if (!hubMessage?.chatID || !userUid) return false;

  if (!partnerUid) {
    const me = uid(userUid);
    return (
      uid(hubMessage.senderUid) === me || uid(hubMessage.receiverUid) === me
    );
  }

  return isReadReceiptRelevant(hubMessage, partnerUid, userUid);
};

export const applyDeleteHubToChatList = (
  prevList,
  hubMessage,
  userUid,
  partnerUid
) => {
  if (!Array.isArray(prevList) || hubMessage?.chatID == null || !userUid) {
    return prevList;
  }

  const me = uid(userUid);
  const chatId = String(hubMessage.chatID);
  const sender = uid(hubMessage.senderUid);
  const receiver = uid(hubMessage.receiverUid);
  const isCompleteDeleted = hubMessage.isCompleteDeleted === true;
  const locallyDeletedId = localStorage.getItem("DeletedChatId");

  if (partnerUid) {
    const partner = uid(partnerUid);
    const inConversation =
      (sender === me && receiver === partner) ||
      (receiver === me && sender === partner);
    if (!inConversation) return prevList;
  } else if (sender !== me && receiver !== me) {
    return prevList;
  }

  let changed = false;

  const next = prevList
    .map((group) => {
      if (!Array.isArray(group?.chats)) return group;

      const nextChats = group.chats.flatMap((chat) => {
        if (String(chat.chatID) !== chatId) return [chat];

        if (!isCompleteDeleted) {
          // Delete for me — only the client that performed the delete should update.
          if (locallyDeletedId == null || String(locallyDeletedId) !== chatId) {
            return [chat];
          }
          changed = true;
          return [];
        }

        const partner = partnerUid ? uid(partnerUid) : null;
        const isSenderView = sender === me;
        const isReceiverView =
          receiver === me && (partner ? sender === partner : sender !== me);

        if (!isSenderView && !isReceiverView) {
          return [chat];
        }

        changed = true;
        return [
          {
            ...chat,
            isDeleted: true,
            isCompleteDeleted: true,
            msg: isSenderView ? hubMessage.sndrSideMsg : hubMessage.rcvrSideMsg,
            isRead: false,
          },
        ];
      });

      if (nextChats.length === 0 && group.chats.length > 0) {
        changed = true;
      }

      return nextChats.length > 0 ? { ...group, chats: nextChats } : null;
    })
    .filter(Boolean);

  if (changed && locallyDeletedId != null && String(locallyDeletedId) === chatId) {
    localStorage.removeItem("DeletedChatId");
  }

  return changed ? consolidateChatListByDate(next) : prevList;
};

export const applyReadAllHubReceipt = ({
  hubData,
  userUid,
  openPartnerUid,
  chatList,
  allUserChats,
}) => {
  const partnerUid =
    resolvePartnerFromReadHub(hubData, userUid) || openPartnerUid;

  if (!partnerUid || !userUid) {
    return { chatList, allUserChats, applied: false };
  }

  cachePartnerSentMessagesRead(partnerUid);

  const payload = toReadReceiptPayload(hubData);
  let nextChatList = chatList;
  let nextAllUserChats = allUserChats;

  const markList = (list) => {
    if (!Array.isArray(list)) return list;
    if (payload.length > 0) {
      const relevant = payload.filter((msg) =>
        isReadReceiptRelevant(msg, partnerUid, userUid)
      );
      if (relevant.length > 0) {
        return applyReadReceiptsToChatList(list, relevant, userUid, partnerUid);
      }
    }
    if (isBulkReadAllForOpenChat(hubData, userUid, partnerUid)) {
      return markAllSentInOpenChatAsRead(list, partnerUid, userUid);
    }
    return markAllSentInOpenChatAsRead(list, partnerUid, userUid);
  };

  if (uid(openPartnerUid) === uid(partnerUid) && Array.isArray(chatList)) {
    nextChatList = markList(chatList);
  }

  if (allUserChats?.[partnerUid]) {
    nextAllUserChats = {
      ...allUserChats,
      [partnerUid]: markList(allUserChats[partnerUid]),
    };
  }

  return {
    chatList: nextChatList,
    allUserChats: nextAllUserChats,
    applied: true,
    partnerUid,
  };
};
